// 9fbef606107a605d69c0edbcd8029e5d
import PropTypes from 'prop-types';
import React, { useState, Fragment } from 'react';
import logger from '@tcp/core/src/utils/loggerInstance';
import ModalList from '../../../atoms/ModalList';
import StockIndicator from '../../StockIndicator';
import ProductCard from '../../../atoms/ProductCard';
import AnimatedListView from '../../AnimatedListView';
import ProductList from '../../../component/ProductList';
import { TileContainer, CardImage } from '../styles/RenderTileView.style';
import {
  goToOutFit,
  getImageUri,
  logTileInteraction,
  trackAdobeAnalytics,
} from '../../../utils/ShopByLookStylitics.util';

const RenderTileView = ({
  itemRef,
  dataList,
  uniqueKey,
  icidParam,
  oosBgColor,
  backToLook,
  isEspotTile,
  activeTileId,
  oosTextColor,
  isMobileView,
  isFullRowView,
  imageClassName,
  isAnimatedList,
  imageHeightVal,
  customImageUrl,
  aspectRatioObj,
  showOosOverlay,
  shopButtonText,
  oosOverlayText,
  animationProps,
  setActiveTileId,
  modalHeaderText,
  tileHeightValue,
  isRedirectToPDP,
  isAnimateToLeft,
  isCarouselDesign,
  carouseItemHeight,
  isOneColumnFullRow,
  containerClassName,
  carouselFlipListSize,
  tileBadgeHeightValue,
  onQuickViewOpenClick,
  carouselItemImageSize,
  adobeAnalyticsProps,
  imageFitTypeOverride,
  shouldAllowMaxHeight,
  isNonClickablePromo,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [showList, setShowList] = useState(false);
  const [showImage, setShowImage] = useState(false);
  const { isSlideAnimation } = animationProps;

  const handleTilePress = ({ id, subItemsId, isOutOfStock, outOfStockItemsId }) => {
    trackAdobeAnalytics(uniqueKey);
    if (isRedirectToPDP) {
      goToOutFit({ id, subItemsId, icidParam });
    } else if (!isOutOfStock) {
      logTileInteraction(id, uniqueKey, outOfStockItemsId, logger);
      if (isMobileView) {
        setModalVisible(true);
      } else {
        if (isAnimatedList && isSlideAnimation) {
          setActiveTileId(id);
          setShowImage(true);
        }
        setShowList(true);
      }
    }
  };

  const handleBackToLook = () => {
    setShowList(false);
    setShowImage(false);
    if (isAnimatedList && isSlideAnimation) {
      setActiveTileId(null);
    }
  };

  const {
    id,
    subItemsId,
    isOutOfStock,
    outOfStockItemsId,
    largeImageUrl = '',
    items: productList = [],
  } = dataList;
  const imageUri = getImageUri(customImageUrl, largeImageUrl);
  const isDimmed = activeTileId && activeTileId !== dataList.id;

  return (
    <Fragment>
      <TileContainer
        ref={itemRef}
        isEspotTile={isEspotTile}
        tileHeight={tileHeightValue}
        className={containerClassName}
        aspectRatioObj={aspectRatioObj}
        badgeHeight={tileBadgeHeightValue}
        isCarouselDesign={isCarouselDesign}
        carouseItemHeight={carouseItemHeight}
        isDimmed={isDimmed}
        isFullRowView={isFullRowView}
        shouldAllowMaxHeight={shouldAllowMaxHeight}
      >
        <ProductCard
          disabled={isOutOfStock}
          onPress={() => handleTilePress({ id, subItemsId, isOutOfStock, outOfStockItemsId })}
          showList={showList}
          showImage={showImage}
          isDimmed={isDimmed}
        >
          <CardImage
            src={imageUri}
            className={imageClassName}
            imageHeight={imageHeightVal}
            isFullRowView={isFullRowView}
            aspectRatioObj={aspectRatioObj}
            isCarouselDesign={isCarouselDesign}
            onLoad={() => setIsImageLoaded(true)}
            carouselItemImageSize={carouselItemImageSize}
            imageFitTypeOverride={imageFitTypeOverride}
          />
          {(!isRedirectToPDP || !isNonClickablePromo) && isImageLoaded && (
            <StockIndicator
              oosBgColor={oosBgColor}
              oosTextColor={oosTextColor}
              isOutOfStock={isOutOfStock}
              showOosOverlay={showOosOverlay}
              oosOverlayText={oosOverlayText}
              isDimmed={isDimmed}
            />
          )}
        </ProductCard>
        {!isNonClickablePromo && (
          <AnimatedListView
            showList={showList}
            backToLook={backToLook}
            productList={productList}
            isFullRowView={isFullRowView}
            isAnimatedList={isAnimatedList}
            shopButtonText={shopButtonText}
            aspectRatioObj={aspectRatioObj}
            animationProps={animationProps}
            isAnimateToLeft={isAnimateToLeft}
            isCarouselDesign={isCarouselDesign}
            isOneColumnFullRow={isOneColumnFullRow}
            onQuickViewOpenClick={onQuickViewOpenClick}
            carouselFlipListSize={carouselFlipListSize}
            handleBackToLook={handleBackToLook}
            adobeAnalyticsProps={adobeAnalyticsProps}
            tileHeight={tileHeightValue}
            shouldAllowMaxHeight={shouldAllowMaxHeight}
          />
        )}
      </TileContainer>
      {!isNonClickablePromo && isMobileView && (
        <ModalList
          visible={modalVisible}
          modalHeaderText={modalHeaderText}
          onClose={() => setModalVisible(false)}
        >
          <ProductList
            isMobileView
            productList={productList}
            shopButtonText={shopButtonText}
            onClose={() => setModalVisible(false)}
            onQuickViewOpenClick={onQuickViewOpenClick}
            adobeAnalyticsProps={adobeAnalyticsProps}
            modalVisible={modalVisible}
          />
        </ModalList>
      )}
    </Fragment>
  );
};

RenderTileView.propTypes = {
  icidParam: PropTypes.string,
  isMobileView: PropTypes.bool,
  oosBgColor: PropTypes.string,
  isFullRowView: PropTypes.bool,
  isAnimatedList: PropTypes.bool,
  oosTextColor: PropTypes.string,
  showOosOverlay: PropTypes.bool,
  isAnimateToLeft: PropTypes.bool,
  isRedirectToPDP: PropTypes.bool,
  customImageUrl: PropTypes.string,
  imageClassName: PropTypes.string,
  isCarouselDesign: PropTypes.bool,
  isOneColumnFullRow: PropTypes.bool,
  aspectRatioObj: PropTypes.shape({}),
  containerClassName: PropTypes.string,
  uniqueKey: PropTypes.string.isRequired,
  itemRef: PropTypes.shape({}).isRequired,
  backToLook: PropTypes.string.isRequired,
  shopButtonText: PropTypes.string.isRequired,
  oosOverlayText: PropTypes.string.isRequired,
  imageHeightVal: PropTypes.number.isRequired,
  tileHeightValue: PropTypes.number.isRequired,
  modalHeaderText: PropTypes.string.isRequired,
  onQuickViewOpenClick: PropTypes.func.isRequired,
  tileBadgeHeightValue: PropTypes.number.isRequired,
  dataList: PropTypes.shape({
    id: PropTypes.number,
    largeImageUrl: PropTypes.string,
    items: PropTypes.arrayOf(PropTypes.shape({})),
    isOutOfStock: PropTypes.bool,
    subItemsId: PropTypes.string,
    outOfStockItemsId: PropTypes.string,
  }).isRequired,
  animationProps: PropTypes.shape({
    isFlipAnimation: PropTypes.bool,
    isSlideAnimation: PropTypes.bool,
  }),
  shouldAllowMaxHeight: PropTypes.bool,
};

RenderTileView.defaultProps = {
  icidParam: '',
  oosBgColor: '',
  oosTextColor: '',
  customImageUrl: '',
  aspectRatioObj: {},
  imageClassName: '',
  isMobileView: false,
  isFullRowView: false,
  isAnimatedList: false,
  showOosOverlay: false,
  isAnimateToLeft: false,
  isRedirectToPDP: false,
  containerClassName: '',
  isCarouselDesign: false,
  isOneColumnFullRow: false,
  animationProps: {
    isFlipAnimation: false,
    isSlideAnimation: false,
  },
  shouldAllowMaxHeight: false,
};

export default RenderTileView;
